// オンライン申込の共通バリデーション

import { EMAIL_REGEXP } from '@this/src/util';

/**
 * バリデーション用の正規表現パターン
 * @property {RegExp} email - メールアドレスの形式チェック
 * @property {RegExp} phone - 電話番号の形式チェック（数字とハイフンのみ）
 * @property {RegExp} postalCode - 郵便番号の形式チェック（XXX-XXXX または XXXXXXX）
 * @property {RegExp} kana - カタカナの形式チェック（全角・半角カタカナ、スペース、括弧、中点を許可）
 * @property {RegExp} accountNumber - 口座番号の形式チェック（数字のみ）
 * @property {RegExp} numericOnly - 数値のみの形式チェック
 */
export const VALIDATION_PATTERNS = {
  email: EMAIL_REGEXP,
  phone: /^[\d-]+$/,
  postalCode: /^\d{3}-?\d{4}$/,
  kana: /^[ァ-ヶｦ-ﾟーｰ\s　()（）・･]+$/,
  accountNumber: /^\d+$/,
  numericOnly: /^\d+$/
} as const;

/**
 * バリデーション制約値
 * @property {number} maxLength - 文字列の最大長（デフォルト: 255文字）
 * @property {number} accountNumberLength - 口座番号の桁数（7桁）
 * @property {number} maxGmv - GMVの最大値（約21億）
 */
export const VALIDATION_CONSTRAINTS = {
  maxLength: 255,
  accountNumberLength: 7,
  maxGmv: 2147483647
} as const;

/**
 * エラーメッセージテンプレート
 * @property {Function} required - 必須項目のエラーメッセージ生成
 * @property {Function} requiredInput - 入力必須のエラーメッセージ生成
 * @property {Function} requiredSelect - 選択必須のエラーメッセージ生成
 * @property {Function} maxLength - 文字数制限のエラーメッセージ生成
 * @property {Function} invalidFormat - 形式エラーのメッセージ生成
 * @property {Function} numericOnly - 数字のみ入力のエラーメッセージ生成
 * @property {string} phoneFormat - 電話番号形式のエラーメッセージ
 * @property {string} postalCodeFormat - 郵便番号形式のエラーメッセージ
 * @property {string} kanaFormat - カナ形式のエラーメッセージ
 * @property {string} emailFormat - メールアドレス形式のエラーメッセージ
 */
export const ERROR_MESSAGE_TEMPLATES = {
  required: (fieldName: string) => `${fieldName}は必須です`,
  requiredInput: (fieldName: string) => `${fieldName}を入力してください`,
  requiredSelect: (fieldName: string) => `${fieldName}を選択してください`,
  maxLength: (fieldName: string, length: number = VALIDATION_CONSTRAINTS.maxLength) =>
    `${fieldName}は${length}文字以内で入力してください`,
  invalidFormat: (fieldName: string) => `${fieldName}の形式が正しくありません`,
  numericOnly: (fieldName: string) => `${fieldName}は数字のみで入力してください`,
  phoneFormat: '電話番号は数字とハイフンのみで入力してください',
  postalCodeFormat: '郵便番号は「000-0000」または「0000000」の形式で入力してください',
  kanaFormat: 'カタカナで入力してください',
  emailFormat: 'メールアドレスの形式が正しくありません'
} as const;

/**
 * 必須チェック
 * @param {string | undefined | null} value - チェック対象の値
 * @param {string} fieldName - フィールド名（エラーメッセージに使用）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateRequired = (value: string | undefined | null, fieldName: string): string | undefined => {
  if (!value || value.toString().trim() === '') {
    return ERROR_MESSAGE_TEMPLATES.requiredInput(fieldName);
  }
  return undefined;
};

/**
 * 文字数制限チェック
 * @param {string | undefined | null} value - チェック対象の値
 * @param {string} fieldName - フィールド名（エラーメッセージに使用、空文字列の場合は項目名なし）
 * @param {number} maxLength - 最大文字数（デフォルト: 255）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateLength = (
  value: string | undefined | null,
  fieldName = '',
  maxLength: number = VALIDATION_CONSTRAINTS.maxLength
): string | undefined => {
  if (!value) return undefined;
  if (value.toString().length > maxLength) {
    // フィールド名が空の場合は項目名なしのシンプルなメッセージ
    if (!fieldName) {
      return `${maxLength}文字以内で入力してください`;
    }
    return ERROR_MESSAGE_TEMPLATES.maxLength(fieldName, maxLength);
  }
  return undefined;
};

/**
 * 形式チェック
 * @param {string | undefined | null} value - チェック対象の値
 * @param {RegExp} pattern - チェックに使用する正規表現パターン
 * @param {string} errorMessage - エラー時に返すメッセージ
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateFormat = (
  value: string | undefined | null,
  pattern: RegExp,
  errorMessage: string
): string | undefined => {
  if (!value || value.toString().trim() === '') return undefined;
  if (!pattern.test(value.toString())) {
    return errorMessage;
  }
  return undefined;
};

/**
 * 文字列フィールドのバリデーション
 * 必須チェック、文字数チェック、形式チェックを統合的に実行
 * @param {string | undefined | null} value - チェック対象の値
 * @param {string} fieldName - フィールド名（エラーメッセージに使用）
 * @param {Object} options - バリデーションオプション
 * @param {boolean} options.required - 必須チェックを行うか（デフォルト: false）
 * @param {number} options.maxLength - 最大文字数（デフォルト: 255）
 * @param {RegExp} options.pattern - 形式チェック用の正規表現パターン
 * @param {string} options.formatErrorMessage - 形式エラー時のメッセージ
 * @param {boolean} options.omitFieldNameInLengthError - 文字数エラー時に項目名を省略するか（デフォルト: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateStringField = (
  value: string | undefined | null,
  fieldName: string,
  options: {
    required?: boolean;
    maxLength?: number;
    pattern?: RegExp;
    formatErrorMessage?: string;
    omitFieldNameInLengthError?: boolean;
  } = {}
): string | undefined => {
  const {
    required = false,
    maxLength = VALIDATION_CONSTRAINTS.maxLength,
    pattern,
    formatErrorMessage,
    omitFieldNameInLengthError = true
  } = options;

  // 必須チェック
  if (required) {
    const requiredError = validateRequired(value, fieldName);
    if (requiredError) return requiredError;
  }

  // 値がない場合は以降のチェックをスキップ
  if (!value || value.toString().trim() === '') {
    return undefined;
  }

  // 文字数チェック（デフォルトでは項目名を省略）
  const lengthFieldName = omitFieldNameInLengthError ? '' : fieldName;
  const lengthError = validateLength(value, lengthFieldName, maxLength);
  if (lengthError) return lengthError;

  // 形式チェック
  if (pattern && formatErrorMessage) {
    const formatError = validateFormat(value, pattern, formatErrorMessage);
    if (formatError) return formatError;
  }

  return undefined;
};

// ============================================================
// 特定フィールド用のバリデーション関数
// ============================================================
/**
 * メールアドレスのバリデーション
 * @param {string | undefined | null} value - チェック対象のメールアドレス
 * @param {string} fieldName - フィールド名（デフォルト: 'メールアドレス'）
 * @param {boolean} required - 必須チェックを行うか（デフォルト: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateEmail = (
  value: string | undefined | null,
  fieldName = 'メールアドレス',
  required = true
): string | undefined => {
  return validateStringField(value, fieldName, {
    required,
    pattern: VALIDATION_PATTERNS.email,
    formatErrorMessage: ERROR_MESSAGE_TEMPLATES.emailFormat
  });
};

/**
 * 電話番号のバリデーション
 * 数字とハイフンのみを許可
 * @param {string | undefined | null} value - チェック対象の電話番号
 * @param {string} fieldName - フィールド名（デフォルト: '電話番号'）
 * @param {boolean} required - 必須チェックを行うか（デフォルト: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validatePhone = (
  value: string | undefined | null,
  fieldName = '電話番号',
  required = true
): string | undefined => {
  return validateStringField(value, fieldName, {
    required,
    pattern: VALIDATION_PATTERNS.phone,
    formatErrorMessage: ERROR_MESSAGE_TEMPLATES.phoneFormat
  });
};

/**
 * 郵便番号のバリデーション
 * XXX-XXXX または XXXXXXX の形式を許可
 * @param {string | undefined | null} value - チェック対象の郵便番号
 * @param {string} fieldName - フィールド名（デフォルト: '郵便番号'）
 * @param {boolean} required - 必須チェックを行うか（デフォルト: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validatePostalCode = (
  value: string | undefined | null,
  fieldName = '郵便番号',
  required = true
): string | undefined => {
  return validateStringField(value, fieldName, {
    required,
    pattern: VALIDATION_PATTERNS.postalCode,
    formatErrorMessage: ERROR_MESSAGE_TEMPLATES.postalCodeFormat
  });
};

/**
 * カナのバリデーション
 * 全角・半角カタカナ、スペース、括弧、中点を許可
 * @param {string | undefined | null} value - チェック対象のカナ文字列
 * @param {string} fieldName - フィールド名
 * @param {boolean} required - 必須チェックを行うか（デフォルト: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateKana = (
  value: string | undefined | null,
  fieldName: string,
  required = true
): string | undefined => {
  return validateStringField(value, fieldName, {
    required,
    pattern: VALIDATION_PATTERNS.kana,
    formatErrorMessage: ERROR_MESSAGE_TEMPLATES.kanaFormat
  });
};

/**
 * 数値のみのバリデーション
 * 半角数字のみを許可
 * @param {string | undefined | null} value - チェック対象の文字列
 * @param {string} fieldName - フィールド名
 * @param {boolean} required - 必須チェックを行うか（デフォルト: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateNumericOnly = (
  value: string | undefined | null,
  fieldName: string,
  required = true
): string | undefined => {
  return validateStringField(value, fieldName, {
    required,
    pattern: VALIDATION_PATTERNS.numericOnly,
    formatErrorMessage: ERROR_MESSAGE_TEMPLATES.numericOnly(fieldName)
  });
};
